import { WritableSignal } from '@angular/core';
import { Observable, take } from 'rxjs';
import { DocumentSections, Entity } from '../../models'; // <- adjust import path
import { setSectionValues } from '../../utils'; // <- if setSectionValues lives elsewhere, fix this import

/** A “single-get” function shape (service.get('/resource/123')). */
export type SingleGetter<T> = (iri: string) => Observable<T>;

/**
 * Run a “row-first” refresh if a single-get is available; otherwise run fallback.
 * - Tries `singleGet(iri)`.
 * - On success, calls `onSingle(fresh)`.
 * - On error or when `singleGet` is undefined, runs `onFallback()`.
 */
const rowFirst = <T>(
  singleGet: SingleGetter<T> | undefined,
  iri: string,
  onSingle: (fresh: T) => void,
  onFallback: () => void,
): void => {
  if (!singleGet) return onFallback();
  singleGet(iri).pipe(take(1)).subscribe({
    next: onSingle,
    error: onFallback,
  });
}

/**
 * Upsert one entity inside a sectioned signal slice.
 * - Replaces the entity if it exists in the section.
 * - Appends it if it doesn’t exist yet.
 */
const upsertInSection = <T extends Entity>(
  signal: WritableSignal<DocumentSections<T> | undefined>,
  sectionIri: string,
  entity: T,
): void => {
  signal.update((sections) => {
    const curr = sections?.[sectionIri] ?? [];
    const next = curr.some((x) => x['@id'] === entity['@id'])
      ? curr.map((x) => (x['@id'] === entity['@id'] ? entity : x))
      : [...curr, entity];
    return setSectionValues(sections, sectionIri, next);
  });
}

/**
 * Replace an entire section’s slice (e.g., just the mappings for a section).
 */
const replaceSectionSlice = <T extends Entity>(
  signal: WritableSignal<DocumentSections<T> | undefined>,
  sectionIri: string,
  items: T[] | undefined,
): void => {
  signal.update((sections) => setSectionValues(sections, sectionIri, items ?? []));
}

/**
 * Remove an entity (by IRI) from a section’s slice.
 */
const removeFromSectionByIri = <T extends Entity>(
  signal: WritableSignal<DocumentSections<T> | undefined>,
  sectionIri: string,
  entityIri: string,
): void => {
  signal.update((sections) => {
    const curr = sections?.[sectionIri] ?? [];
    const next = curr.filter((x) => x['@id'] !== entityIri);
    return setSectionValues(sections, sectionIri, next);
  });
}

/**
 * Utility to scan a sectioned slice and find the sectionIRI that contains a given entity IRI.
 * Handy when you don’t have a precomputed index.
 */
const findSectionIriByEntityIri = <T extends Entity>(
  sections: DocumentSections<T> | undefined,
  entityIri: string,
): string | undefined  => {
  if (!sections) return undefined;
  for (const [secIri, arr] of Object.entries(sections)) {
    if ((arr ?? []).some((e) => e['@id'] === entityIri)) return secIri;
  }
  return undefined;
}


export { rowFirst, upsertInSection, replaceSectionSlice, removeFromSectionByIri, findSectionIriByEntityIri };
